import {useContext} from 'react'

import {FieldsConfiguration} from '../fields/types'

import {
  CommonConfigurationContext,
  CommonFieldsConfiguration
} from './CommonConfigurationContext'
import {ConfigurationContext} from './ConfigurationContext'

export const useConfigurationContext = (): FieldsConfiguration => {
  const context = useContext(ConfigurationContext)

  if (!context) {
    throw new Error('useConfigurationContext must be used within a ConfigurationContextProvider')
  }

  return context
}

export const useFieldConfigurationContext = <T extends keyof FieldsConfiguration>(
  key: T
): FieldsConfiguration[T] => {
  const configuration = useContext(ConfigurationContext)
  const commonConfiguration = useContext(CommonConfigurationContext)
  const commonFieldConfig =
    key === 'dateTime'
      ? undefined
      : commonConfiguration?.[key as keyof CommonFieldsConfiguration]
  const fieldConfig = configuration?.[key] ?? commonFieldConfig

  if (fieldConfig === undefined) {
    if (key === 'dateTime' && !configuration && commonConfiguration) {
      throw new Error(
        'useFieldConfigurationContext("dateTime") requires a V2 ConfigurationContext. Use V3 date-time hooks with a V3 configuration provider.'
      )
    }

    if (!configuration && !commonConfiguration) {
      throw new Error('useFieldConfigurationContext must be used within a configuration provider')
    }

    console.warn(
      `Field configuration for '${String(key)}' is undefined. Available configurations:`,
      Object.keys(configuration ?? commonConfiguration ?? {})
    )
    // Return a default configuration instead of throwing
    return {enabled: false} as FieldsConfiguration[T]
  }

  return fieldConfig as FieldsConfiguration[T]
}
